import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Map, Navigation, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export type MapsApp = 'google' | 'apple' | 'waze' | 'system';

interface MapsAppOption {
  id: MapsApp;
  name: string;
  description: string;
  icon: React.ReactNode;
  platforms: ('ios' | 'android' | 'web')[];
}

const MAPS_APPS: MapsAppOption[] = [
  {
    id: 'google',
    name: 'Google Maps',
    description: 'Most popular maps app with detailed navigation',
    icon: <Map className="w-8 h-8 text-blue-600" />,
    platforms: ['ios', 'android', 'web']
  },
  {
    id: 'apple',
    name: 'Apple Maps',
    description: 'Built-in iOS navigation with Siri integration',
    icon: <Navigation className="w-8 h-8 text-gray-600" />,
    platforms: ['ios']
  },
  {
    id: 'waze',
    name: 'Waze',
    description: 'Crowd-sourced navigation with real-time updates',
    icon: <Smartphone className="w-8 h-8 text-orange-500" />,
    platforms: ['ios', 'android']
  },
  {
    id: 'system',
    name: 'System Default',
    description: 'Use your device\'s default maps app',
    icon: <Smartphone className="w-8 h-8 text-green-600" />,
    platforms: ['ios', 'android', 'web']
  }
];

interface MapsAppSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectApp: (app: MapsApp) => void;
  currentSelection?: MapsApp;
  isFirstTime?: boolean;
}

export default function MapsAppSelectionDialog({
  isOpen,
  onClose,
  onSelectApp,
  currentSelection,
  isFirstTime = false
}: MapsAppSelectionDialogProps) {
  const [selectedApp, setSelectedApp] = useState<MapsApp | null>(currentSelection || null);

  // Filter apps based on platform
  const getPlatform = (): 'ios' | 'android' | 'web' => {
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
        return 'ios';
      } else if (userAgent.includes('android')) {
        return 'android';
      }
    }
    return 'web';
  };

  const platform = getPlatform();
  const availableApps = MAPS_APPS.filter(app => app.platforms.includes(platform));

  const handleSelectApp = (app: MapsApp) => {
    setSelectedApp(app);
  };

  const handleConfirm = () => {
    if (selectedApp) {
      onSelectApp(selectedApp);
      onClose();
      toast.success(`Default maps app set to ${MAPS_APPS.find(app => app.id === selectedApp)?.name}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-auto"
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-gray-900">
                {isFirstTime ? 'Choose Your Maps App' : 'Select Default Maps App'}
              </CardTitle>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {isFirstTime && (
              <p className="text-sm text-gray-600 mt-2">
                When you choose external navigation, we'll open your selected maps app for turn-by-turn directions.
                You can change this anytime in Settings.
              </p>
            )}
          </CardHeader>

          <CardContent className="space-y-3">
            {availableApps.map((app) => (
              <motion.div
                key={app.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedApp === app.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleSelectApp(app.id)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {app.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{app.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{app.description}</p>
                    {currentSelection === app.id && (
                      <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        Currently Selected
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            <div className="flex gap-3 pt-4">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedApp}
                className="flex-1"
              >
                {isFirstTime ? 'Continue' : 'Save Selection'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}